import React, { useState, useEffect } from 'react'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import FilterTabs from '../reservation/partials/FilterTabs'
import Api from '../../../_helpers/Api'
import { trans } from '../../../_providers/Translation'

//partials
import TaskInfo from '../reservation/partials/TaskInfo'
import Parts from '../reservation/partials/parts/Index'
import Images from '../reservation/partials/Images'
import Comments from '../reservation/partials/Comments'
import Todo from '../reservation/partials/Todo'
import Invoices from '../reservation/partials/Invoices'
import History from '../reservation/partials/History'
import loader from '../../../assets/img/loader.svg'

// styles
import "@/assets/scss/pages/reservations.scss"

function SharedReservationPreview() {
    const params = useParams()
    const history = useHistory()
    const location = useLocation()

    const id = params.id
    const searchParams = new URLSearchParams(location.search)

    const tabs = ['info', 'parts', 'images', 'todo', 'comments', 'invoices', 'history']
    const tab = searchParams.get('tab')

    const [reservation, setReservation] = useState({
        tab: tabs.indexOf(tab) > -1 ? tab : tabs[0],
        data: {
            can_modify: false,
            can_modify_appliance: false,
            can_modify_initial_status: false,
            can_reassign: false,
            contractor: {},
            customer: {},
            appliancecategory: {},
            appliancebrand: {},
            appliancemodel: {},
            diagnostic_price: 0,
            transport_price: 0,
            labour_price: 0,
            parts_price: 0,
            calculate_labour_price: true
        },
        tabsCount: {
            commentsCount: 0,
            imagesCount: 0,
            partsCount: 0,
            todosCount: 0,
            manualsCount: 0,
            invoicesCount: 0
        },
        update: false,
        loading: true,
    })

    const [settings, setSettings] = useState({
        data: null,
        loading: true,
    })


    useEffect(() => {
        setReservation(prev => ({ ...prev, loading: true }))

        Api.get(`shared-reservations/${id}/preview`)
            .then(res => {
                setReservation(prev => ({
                    ...prev,
                    data: {
                        // spread flat reservation fields
                        ...(res.data.reservation || {}),
                        // top-level fields
                        id: res.data.reservation?.id,
                        address: res.data.reservation?.address || res.data.address || '',
                        customer: res.data.customer || {},
                        contractor: res.data.contractor || {},
                        // map flat appliance strings to nested objects TaskInfo expects
                        appliances: Array.isArray(res.data.appliances)
                            ? res.data.appliances.map(ap => ({
                                ...ap,
                                appliancecategory: ap.category ? { name: ap.category } : null,
                                appliancebrand: ap.brand ? { name: ap.brand } : null,
                                appliancemodel: ap.model ? { name: ap.model } : null,
                            }))
                            : [],
                        // derive visit_date_ymd from ISO visit_date
                        visit_date_ymd: res.data.reservation?.visit_date
                            ? res.data.reservation.visit_date.substring(0, 10)
                            : null,
                        // keep raw string for display in the preview info row
                        visit_period: res.data.reservation?.visit_period || null,
                        // map visit_period string to object TaskInfo expects
                        visitperiod: res.data.reservation?.visit_period
                            ? { period: res.data.reservation.visit_period }
                            : null,
                        // map employee string to performer object
                        performer: res.data.reservation?.employee
                            ? { name: res.data.reservation.employee, lastname: '' }
                            : null,
                        // shared reservation id for future use
                        shared_reservation_id: res.data.shared_reservation?.id || null,
                        // lock all modification flags
                        can_modify: false,
                        can_modify_appliance: false,
                        can_modify_initial_status: false,
                        can_modify_final_status: false,
                        can_modify_parts: false,
                        can_reassign: false,
                        can_send_report: false,
                        can_send_confirmation: false,
                        can_send_completion_message: false,
                    },
                    tabsCount: {
                        commentsCount: res.data.comments_count ?? prev.tabsCount.commentsCount,
                        imagesCount: res.data.images_count ?? prev.tabsCount.imagesCount,
                        partsCount: res.data.parts_count ?? prev.tabsCount.partsCount,
                        todosCount: res.data.todos_count ?? prev.tabsCount.todosCount,
                        manualsCount: res.data.manuals_count ?? prev.tabsCount.manualsCount,
                        invoicesCount: res.data.invoices_count ?? prev.tabsCount.invoicesCount
                    },
                    loading: false,
                }))
            })
            .catch(err => {
                if (err.response?.status === 404) {
                    history.push('/reservations')
                }
                setReservation(prev => ({ ...prev, loading: false }))
            })
    }, [id, reservation.update])

    useEffect(() => {
        Api.get('partners/settings/pricing')
            .then(res => {
                setSettings({ data: res.data, loading: false })
            })
            .catch(() => {
                setSettings(prev => ({ ...prev, loading: false }))
            })
    }, [])

    const handleTab = newValue => {
        const currentParams = new URLSearchParams(location.search)
        currentParams.set('tab', newValue)
        history.push(`?${currentParams.toString()}`)
        setReservation(prev => ({ ...prev, tab: newValue }))
    }

    const setOriginal = data => {
        setReservation(prev => ({ ...prev, data }))
    }

    const refreshData = () => {
        setReservation(prev => ({ ...prev, update: new Date().getTime() }))
    }

    return (
        <div className="wrapper">
            <div className="top-part">
                <div className="left">
                    <h1 className="page-heading">
                        {`${trans('headings.sharedReservationPreview')} #${reservation.data.reference_code || ''}`}
                    </h1>
                    <p className="page-subheading">
                        {trans('subheadings.createEditReservations')}
                    </p>
                </div>
            </div>

            <FilterTabs
                editMode={false}
                tab={reservation.tab}
                loading={reservation.loading}
                reservationId={id}
                referenceCode={reservation.data.reference_code}
                categoryId={reservation.data.appliance_category_id}
                brandId={reservation.data.appliance_brand_id}
                modelId={reservation.data.appliance_model_id}
                model={reservation.data.appliancemodel ? reservation.data.appliancemodel.name : ''}
                tabsCount={reservation.tabsCount}
                handleTab={handleTab}
            />

            <div style={{ display: reservation.tab === 'info' ? 'block' : 'none' }}>
                <style>{`.shared-preview-wrapper form .buttons-holder { display: none !important; } .top-part .button-default { display: none !important; }`}</style>
                {!reservation.loading && (reservation.data.visit_period || reservation.data.appliances?.some(ap => ap.appliancecategory?.name)) &&
                <>
                    <label>Original data</label>
                    <div className="row" style={{ marginBottom: 16, gap: 24 }}>
                        {reservation.data.appliances?.map((ap, i) => ap.appliancecategory?.name &&
                            <div className="col" key={'cat-' + i}>
                                <label>{trans('labels.category')}</label>
                                <input type="text" value={ap.appliancecategory.name} disabled />
                            </div>
                        )}
                        {reservation.data.appliances?.map((ap, i) => ap.appliancebrand?.name &&
                            <div className="col" key={'cat-' + i}>
                                <label>{trans('labels.brand')}</label>
                                <input type="text" value={ap.appliancebrand.name} disabled />
                            </div>
                        )}
                    </div>
                    <div className="row" style={{ marginBottom: 16, gap: 24 }}>
                        {reservation.data.visit_period &&
                            <div className="col">
                                <label>{trans('tables.visitHour')}</label>
                                <input type="text" value={reservation.data.visit_period} disabled />
                            </div>
                        }
                    </div>
                </>
                }
                <div className="shared-preview-wrapper">
                    <TaskInfo
                        addMode={false}
                        loading={reservation.loading || settings.loading}
                        data={reservation.data}
                        settings={settings.data}
                        tabsCount={reservation.tabsCount}
                        setOriginal={setOriginal}
                        isAddress={() => false}
                        isOnSiteRepair={() => false}
                        getResTypeId={() => reservation.data.res_type_id}
                        getResType={() => reservation.data.res_type_id}
                        handleSetLoadingOnDelete={() => {}}
                    />
                    <div className="row buttons-holder">
                        <button className="button button-default" onClick={() => history.push('/reservations')}>
                            {trans('buttons.cancel')}
                        </button>
                    </div>
                </div>
            </div>

            {(reservation.loading || settings.loading) && reservation.tab !== 'info'
                ?
                <img src={loader} alt="loading" />
                :
                (() => {
                    switch (reservation.tab) {
                        case 'parts':
                            return <Parts data={reservation.data} refreshData={refreshData} />
                        case 'images':
                            return <Images data={reservation.data} refreshData={refreshData} />
                        case 'todo':
                            return <Todo data={reservation.data} refreshData={refreshData} />
                        case 'comments':
                            return <Comments data={reservation.data} refreshData={refreshData} />
                        case 'invoices':
                            return <Invoices data={reservation.data} refreshData={refreshData} />
                        case 'history':
                            return <History data={reservation.data} refreshData={refreshData} />
                        default:
                            return ''
                    }
                })()
            }
        </div>
    )
}

export default SharedReservationPreview
